import { useState, useEffect, useMemo, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { llcApi, payeeApi } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Pencil, Trash2, Users, Search, ArrowUpDown, ArrowUp, ArrowDown, FileText, Calendar, Printer, Download, Upload, Archive, ArchiveRestore } from 'lucide-react';
import { toast } from 'sonner';
import { Form1099PrintView } from '../components/Form1099PrintView';

const initialFormData = {
  name: '',
  email: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  zip_code: '',
  bank_name: '',
  bank_routing: '',
  bank_account: '',
  llc_ids: [],
  requires_1099: false,
  tax_id: '',
  form_1099_type: '1099-NEC',
};

const form1099Types = [
  { value: '1099-NEC', label: '1099-NEC (Nonemployee Compensation)' },
  { value: '1099-MISC', label: '1099-MISC (Miscellaneous Income)' },
  { value: '1099-DIV', label: '1099-DIV (Dividends and Distributions)' },
];

const currentYear = new Date().getFullYear();
const availableYears = [currentYear, currentYear + 1];

export const PayeeManagementPage = () => {
  const [payees, setPayees] = useState([]);
  const [llcs, setLlcs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPayee, setEditingPayee] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [archivedDupe, setArchivedDupe] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  
  // 1099 Modal State
  const [show1099Modal, setShow1099Modal] = useState(false);
  const [selected1099Payee, setSelected1099Payee] = useState(null);
  const [selected1099Year, setSelected1099Year] = useState(null);
  const [form1099Data, setForm1099Data] = useState(null);
  const [loading1099, setLoading1099] = useState(false);
  
  const form1099PrintRef = useRef(null);
  
  // Transaction modal state
  const [showTransactions, setShowTransactions] = useState(false);
  const [transPayeeName, setTransPayeeName] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [transLoading, setTransLoading] = useState(false);
  const [transSortCol, setTransSortCol] = useState('date');
  const [transSortDir, setTransSortDir] = useState('desc');
  
  const handlePrint1099 = useReactToPrint({
    contentRef: form1099PrintRef,
    documentTitle: `1099-NEC-${selected1099Payee?.name}-${selected1099Year}`,
  });

  useEffect(() => {
    fetchData();
  }, [showArchived]);

  const fetchData = async () => {
    try {
      const [payeesRes, llcsRes] = await Promise.all([
        payeeApi.list(null, showArchived),
        llcApi.list(),
      ]);
      setPayees(payeesRes.data);
      setLlcs(llcsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleLLCToggle = (llcId) => {
    setFormData((prev) => ({
      ...prev,
      llc_ids: prev.llc_ids.includes(llcId)
        ? prev.llc_ids.filter((id) => id !== llcId)
        : [...prev.llc_ids, llcId],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate Tax ID if 1099 is required
    if (formData.requires_1099 && !formData.tax_id?.trim()) {
      toast.error('Tax ID is required for payees receiving 1099 forms');
      return;
    }
    
    setSubmitting(true);
    try {
      if (editingPayee) {
        await payeeApi.update(editingPayee.id, formData);
        toast.success('Payee updated successfully');
      } else {
        await payeeApi.create(formData);
        toast.success('Payee created successfully');
      }
      setDialogOpen(false);
      setEditingPayee(null);
      setFormData(initialFormData);
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      if (detail.startsWith('ARCHIVED_DUPLICATE:')) {
        const parts = detail.split(':');
        const archivedId = parts[1];
        const archivedName = parts.slice(2).join(':');
        setDialogOpen(false);
        setArchivedDupe({ id: archivedId, name: archivedName });
      } else {
        toast.error(detail || 'Operation failed');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (payee) => {
    setEditingPayee(payee);
    setFormData({
      name: payee.name,
      email: payee.email || '',
      address_line1: payee.address_line1 || '',
      address_line2: payee.address_line2 || '',
      city: payee.city || '',
      state: payee.state || '',
      zip_code: payee.zip_code || '',
      bank_name: payee.bank_name || '',
      bank_routing: payee.bank_routing || '',
      bank_account: payee.bank_account || '',
      llc_ids: payee.llc_ids || [],
      requires_1099: payee.requires_1099 || false,
      tax_id: payee.tax_id || '',
      form_1099_type: payee.form_1099_type || '1099-NEC',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await payeeApi.delete(id);
      toast.success('Payee deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete payee');
    }
  };

  const openNewDialog = () => {
    setEditingPayee(null);
    setFormData(initialFormData);
    setDialogOpen(true);
  };

  const getLLCNames = (llcIds) => {
    return llcIds
      .map((id) => llcs.find((llc) => llc.id === id)?.company_name)
      .filter(Boolean)
      .join(', ');
  };

  const getLLC = (llcId) => {
    return llcs.find((llc) => llc.id === llcId);
  };

  // 1099 Modal Functions
  const handle1099Click = (payee) => {
    setSelected1099Payee(payee);
    setSelected1099Year(null);
    setForm1099Data(null);
    setShow1099Modal(true);
  };

  const handleYearSelect = async (year) => {
    setSelected1099Year(year);
    setLoading1099(true);
    try {
      const response = await payeeApi.get1099Data(selected1099Payee.id, year);
      setForm1099Data(response.data);
    } catch (error) {
      toast.error('Failed to load 1099 data');
      console.error(error);
    } finally {
      setLoading1099(false);
    }
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown className="w-4 h-4 ml-1 opacity-50" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-4 h-4 ml-1" />
      : <ArrowDown className="w-4 h-4 ml-1" />;
  };

  const filteredAndSortedPayees = useMemo(() => {
    let result = [...payees];
    
    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(payee => 
        payee.name.toLowerCase().includes(term) ||
        payee.city?.toLowerCase().includes(term) ||
        payee.state?.toLowerCase().includes(term) ||
        payee.bank_name?.toLowerCase().includes(term)
      );
    }
    
    // Sort
    result.sort((a, b) => {
      let aVal, bVal;
      
      switch (sortConfig.key) {
        case 'name':
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case 'city':
          aVal = (a.city || '').toLowerCase();
          bVal = (b.city || '').toLowerCase();
          break;
        case 'llcs':
          aVal = a.llc_ids?.length || 0;
          bVal = b.llc_ids?.length || 0;
          break;
        case 'bank':
          aVal = (a.bank_name || '').toLowerCase();
          bVal = (b.bank_name || '').toLowerCase();
          break;
        default:
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return result;
  }, [payees, searchTerm, sortConfig]);

  const sortedTransactions = useMemo(() => {
    return [...transactions].sort((a, b) => {
      const dir = transSortDir === 'asc' ? 1 : -1;
      switch (transSortCol) {
        case 'date': return dir * (new Date(a.date) - new Date(b.date));
        case 'payer': return dir * (a.payer || '').localeCompare(b.payer || '');
        case 'memo': return dir * (a.memo || '').localeCompare(b.memo || '');
        case 'amount': return dir * ((a.amount || 0) - (b.amount || 0));
        default: return 0;
      }
    });
  }, [transactions, transSortCol, transSortDir]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-slate-500">Loading payees...</div>
      </div>
    );
  }

  const openTransactions = async (payeeName) => {
    setTransPayeeName(payeeName);
    setShowTransactions(true);
    setTransLoading(true);
    try {
      const res = await payeeApi.getTransactions(payeeName);
      setTransactions(res.data);
    } catch {
      toast.error('Failed to load transactions');
    }
    setTransLoading(false);
  };

  const toggleTransSort = (col) => {
    if (transSortCol === col) {
      setTransSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setTransSortCol(col);
      setTransSortDir('asc');
    }
  };

  const TransSortHeader = ({ col, children, className = '' }) => (
    <th className={`cursor-pointer select-none hover:bg-slate-100 ${className}`} onClick={() => toggleTransSort(col)}>
      <span className="inline-flex items-center gap-1">
        {children}
        {transSortCol === col ? (transSortDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-30" />}
      </span>
    </th>
  );

  const handleExportCsv = async () => {
    try {
      const response = await payeeApi.exportExcel();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'payees.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Payees exported successfully');
    } catch {
      toast.error('Failed to export payees');
    }
  };

  const handleImportCsv = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const response = await payeeApi.importExcel(file);
      toast.success(`Imported ${response.data.imported} payees (${response.data.skipped} skipped)`);
      fetchPayees();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to import payees');
    }
    e.target.value = '';
  };

  const handleToggleArchive = async (payeeId) => {
    try {
      const res = await payeeApi.toggleArchive(payeeId);
      toast.success(`Payee ${res.data.archived ? 'archived' : 'restored'}`);
      fetchData();
    } catch (e) {
      toast.error('Failed to update payee');
    }
  };

  return (
    <div className="space-y-3 fade-in" data-testid="payee-management-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-lg font-semibold text-slate-900 tracking-tight">Payee Management</h1>
          <p className="text-slate-500 mt-1">
            {showArchived ? `All payees (${payees.filter(p => p.archived).length} archived)` : `${payees.length} active payees`}
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <Button variant="outline" onClick={handleExportCsv} data-testid="export-payees-btn">
            <Download className="w-4 h-4 mr-2" />
            Download Excel
          </Button>
          <Button variant="outline" onClick={() => document.getElementById('import-csv-input').click()} data-testid="import-payees-btn">
            <Upload className="w-4 h-4 mr-2" />
            Upload Excel
          </Button>
          <Button variant={showArchived ? "default" : "outline"} onClick={() => setShowArchived(p => !p)} data-testid="toggle-archived-btn" className={showArchived ? "bg-amber-600 hover:bg-amber-700" : ""}>
            <Archive className="w-4 h-4 mr-2" />
            {showArchived ? 'Showing All' : 'Show Archived'}
          </Button>
          <input id="import-csv-input" type="file" accept=".xlsx,.csv" className="hidden" onChange={handleImportCsv} />
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNewDialog} className="bg-slate-900 hover:bg-slate-800" data-testid="add-payee-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Payee
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle>{editingPayee ? 'Edit Payee' : 'Add New Payee'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="name">Payee Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  data-testid="payee-name-input"
                />
              </div>

              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="payee@example.com"
                  data-testid="payee-email-input"
                />
              </div>

              <div>
                <Label htmlFor="address_line1">Address Line 1</Label>
                <Input
                  id="address_line1"
                  name="address_line1"
                  value={formData.address_line1}
                  onChange={handleInputChange}
                />
              </div>

              <div>
                <Label htmlFor="address_line2">Address Line 2</Label>
                <Input
                  id="address_line2"
                  name="address_line2"
                  value={formData.address_line2}
                  onChange={handleInputChange}
                />
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                  />
                </div>
                <div>
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    maxLength={2}
                  />
                </div>
                <div>
                  <Label htmlFor="zip_code">ZIP</Label>
                  <Input
                    id="zip_code"
                    name="zip_code"
                    value={formData.zip_code}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="pt-4 border-t">
                <h4 className="text-sm font-medium text-slate-700 mb-3">Bank Information (for Wire Transfers)</h4>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="bank_name">Bank Name</Label>
                    <Input
                      id="bank_name"
                      name="bank_name"
                      value={formData.bank_name}
                      onChange={handleInputChange}
                      placeholder="e.g., Chase Bank"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label htmlFor="bank_routing">Routing Number</Label>
                      <Input
                        id="bank_routing"
                        name="bank_routing"
                        value={formData.bank_routing}
                        onChange={handleInputChange}
                        placeholder="9 digits"
                        className="font-mono"
                      />
                    </div>
                    <div>
                      <Label htmlFor="bank_account">Account Number</Label>
                      <Input
                        id="bank_account"
                        name="bank_account"
                        value={formData.bank_account}
                        onChange={handleInputChange}
                        className="font-mono"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <Label>Associated LLCs</Label>
                <p className="text-xs text-slate-500">Select which LLCs can write checks to this payee</p>
                {llcs.length === 0 ? (
                  <p className="text-sm text-slate-500">No LLCs available. Add an LLC first.</p>
                ) : (
                  <div className="space-y-2 max-h-40 overflow-y-auto border rounded-md p-3">
                    {llcs.map((llc) => (
                      <div key={llc.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`llc-${llc.id}`}
                          checked={formData.llc_ids.includes(llc.id)}
                          onCheckedChange={() => handleLLCToggle(llc.id)}
                          data-testid={`llc-checkbox-${llc.id}`}
                        />
                        <label
                          htmlFor={`llc-${llc.id}`}
                          className="text-sm font-medium leading-none cursor-pointer"
                        >
                          {llc.company_name}
                        </label>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="pt-4 border-t">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="requires_1099" className="text-sm font-medium">IRS Form 1099</Label>
                    <p className="text-xs text-slate-500 mt-0.5">Will this payee receive a 1099 form?</p>
                  </div>
                  <Switch
                    id="requires_1099"
                    checked={formData.requires_1099}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, requires_1099: checked, tax_id: checked ? prev.tax_id : '' }))}
                    data-testid="payee-1099-switch"
                  />
                </div>
                
                {formData.requires_1099 && (
                  <div className="mt-4 p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-4">
                    <div>
                      <Label htmlFor="form_1099_type" className="text-sm font-medium">
                        Form Type <span className="text-red-500">*</span>
                      </Label>
                      <Select
                        value={formData.form_1099_type}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, form_1099_type: value }))}
                      >
                        <SelectTrigger className="mt-1.5" data-testid="payee-1099-type-select">
                          <SelectValue placeholder="Select 1099 type" />
                        </SelectTrigger>
                        <SelectContent>
                          {form1099Types.map(type => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor="tax_id" className="text-sm font-medium">
                        Tax ID (SSN or EIN) <span className="text-red-500">*</span>
                      </Label>
                      <p className="text-xs text-slate-500 mt-0.5 mb-1.5">Required for 1099 reporting</p>
                      <Input
                        id="tax_id"
                        name="tax_id"
                        value={formData.tax_id}
                        onChange={handleInputChange}
                        placeholder="XXX-XX-XXXX or XX-XXXXXXX"
                        required={formData.requires_1099}
                        data-testid="payee-tax-id-input"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} data-testid="payee-submit-btn">
                  {submitting ? 'Saving...' : editingPayee ? 'Update Payee' : 'Create Payee'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Search and Stats Bar */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search payees by name, city, state, or bank..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="payee-search-input"
          />
        </div>
        <div className="text-sm text-slate-500">
          {filteredAndSortedPayees.length} of {payees.length} payees
        </div>
      </div>

      {/* Payee List Card */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {payees.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Users className="w-12 h-12 text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900">No Payees Added</h3>
              <p className="text-slate-500 mt-1 mb-4">Add payees to quickly select them when creating checks</p>
              <Button onClick={openNewDialog} data-testid="empty-add-payee-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Payee
              </Button>
            </div>
          ) : filteredAndSortedPayees.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              No payees match your search
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table w-full">
                <thead className="sticky top-0 z-10">
                  <tr style={{ backgroundColor: '#1e293b', color: '#ffffff' }}>
                    <th className="cursor-pointer select-none" onClick={() => handleSort('name')} style={{ color: '#ffffff', borderBottom: 'none', width: '200px', minWidth: '200px' }}>
                      <span className="flex items-center gap-1.5">Name {getSortIcon('name')}</span>
                    </th>
                    <th className="cursor-pointer select-none" onClick={() => handleSort('city')} style={{ color: '#ffffff', borderBottom: 'none', width: '200px', minWidth: '200px' }}>
                      <span className="flex items-center gap-1.5">Address {getSortIcon('city')}</span>
                    </th>
                    <th className="cursor-pointer select-none" onClick={() => handleSort('bank')} style={{ color: '#ffffff', borderBottom: 'none' }}>
                      <span className="flex items-center gap-1.5">Bank {getSortIcon('bank')}</span>
                    </th>
                    <th className="cursor-pointer select-none" onClick={() => handleSort('llcs')} style={{ color: '#ffffff', borderBottom: 'none' }}>
                      <span className="flex items-center gap-1.5">LLCs {getSortIcon('llcs')}</span>
                    </th>
                    <th style={{ color: '#ffffff', borderBottom: 'none' }}>Email</th>
                    <th className="text-center" style={{ color: '#ffffff', borderBottom: 'none' }}>1099</th>
                    <th className="text-right" style={{ color: '#ffffff', borderBottom: 'none' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedPayees.map((payee) => (
                    <tr key={payee.id} className={`hover:bg-slate-50 ${payee.archived ? 'opacity-50 bg-slate-50' : ''}`}>
                      <td className="font-medium">
                        <button
                          className="text-blue-700 hover:text-blue-900 hover:underline cursor-pointer text-left"
                          onClick={() => openTransactions(payee.name)}
                          data-testid={`payee-name-link-${payee.id}`}
                        >
                          {payee.name}
                        </button>
                        {payee.archived && (
                          <span className="ml-2 text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">Archived</span>
                        )}
                      </td>
                      <td className="text-slate-500 text-sm">
                        {payee.address_line1 ? (
                          <div className="leading-tight">
                            <div>{payee.address_line1}</div>
                            {payee.address_line2 && <div>{payee.address_line2}</div>}
                            <div>
                              {payee.city}{payee.city && payee.state ? ', ' : ''}{payee.state} {payee.zip_code}
                            </div>
                          </div>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="text-sm">
                        {payee.bank_name ? (
                          <div>
                            <span className="text-slate-700">{payee.bank_name}</span>
                            {payee.bank_routing && (
                              <span className="text-slate-400 text-xs ml-2 font-mono">
                                ({payee.bank_routing})
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td>
                        {payee.llc_ids && payee.llc_ids.length > 0 ? (
                          <span className="text-sm text-slate-600">{getLLCNames(payee.llc_ids)}</span>
                        ) : (
                          <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                            No LLCs assigned
                          </span>
                        )}
                      </td>
                      <td className="text-sm text-slate-600">{payee.email || '-'}</td>
                      <td className="text-center">
                        {payee.requires_1099 ? (
                          <button
                            onClick={() => handle1099Click(payee)}
                            className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 px-2 py-1 rounded hover:bg-green-100 transition-colors cursor-pointer"
                            data-testid={`1099-btn-${payee.id}`}
                          >
                            <FileText className="w-3 h-3" />
                            Yes
                          </button>
                        ) : (
                          <span className="text-xs text-slate-400">No</span>
                        )}
                      </td>
                      <td className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleToggleArchive(payee.id)}
                            title={payee.archived ? 'Unarchive' : 'Archive'}
                            data-testid={`archive-payee-${payee.id}`}
                            className={payee.archived ? 'text-green-600 hover:text-green-700' : 'text-amber-600 hover:text-amber-700'}
                          >
                            {payee.archived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handleEdit(payee)} 
                            data-testid={`edit-payee-${payee.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="text-red-600 hover:text-red-700" 
                                data-testid={`delete-payee-${payee.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Payee?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently delete {payee.name}. This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction 
                                  onClick={() => handleDelete(payee.id)} 
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 1099 Modal */}
      <Dialog open={show1099Modal} onOpenChange={(open) => {
        setShow1099Modal(open);
        if (!open) {
          setSelected1099Payee(null);
          setSelected1099Year(null);
          setForm1099Data(null);
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {selected1099Payee?.form_1099_type || '1099-NEC'} for {selected1099Payee?.name}
            </DialogTitle>
          </DialogHeader>
          
          {!selected1099Year ? (
            // Year Selection View
            <div className="py-6">
              <p className="text-sm text-slate-500 mb-4">Select a tax year to generate the {selected1099Payee?.form_1099_type || '1099-NEC'} form:</p>
              <div className="grid grid-cols-2 gap-4">
                {availableYears.map(year => (
                  <button
                    key={year}
                    onClick={() => handleYearSelect(year)}
                    className="p-6 border-2 border-slate-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors flex flex-col items-center gap-2"
                    data-testid={`year-btn-${year}`}
                  >
                    <Calendar className="w-8 h-8 text-slate-400" />
                    <span className="text-2xl font-bold text-slate-900">{year}</span>
                    <span className="text-sm text-slate-500">Tax Year</span>
                  </button>
                ))}
              </div>
            </div>
          ) : loading1099 ? (
            // Loading State
            <div className="py-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto"></div>
              <p className="text-slate-500 mt-4">Loading 1099 data...</p>
            </div>
          ) : form1099Data ? (
            // 1099 Form View — one per LLC
            <div>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <Button variant="outline" size="sm" onClick={() => setSelected1099Year(null)}>
                    ← Back to Years
                  </Button>
                  <div className="text-sm text-slate-500">
                    <span className="font-medium text-slate-700">{form1099Data.check_count}</span> checks, 
                    <span className="font-medium text-slate-700 ml-1">{form1099Data.wire_count}</span> wires
                    {(form1099Data.per_llc || []).length > 1 && (
                      <span className="ml-2 text-slate-400">across <span className="font-medium text-slate-700">{form1099Data.per_llc.length}</span> LLCs</span>
                    )}
                  </div>
                </div>
                <Button onClick={handlePrint1099} data-testid="print-1099-btn">
                  <Printer className="w-4 h-4 mr-2" />
                  Print {selected1099Payee?.form_1099_type || '1099'}
                </Button>
              </div>
              
              {form1099Data.total_amount === 0 ? (
                <div className="py-8 text-center border rounded-lg bg-slate-50">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-600">No payments found for {selected1099Year}</p>
                  <p className="text-sm text-slate-400 mt-1">Total compensation: $0.00</p>
                </div>
              ) : (
                <div ref={form1099PrintRef} className="space-y-8">
                  {(form1099Data.per_llc || []).map((llcEntry, idx) => (
                    <div key={llcEntry.llc_id} className="border rounded-lg overflow-hidden">
                      {(form1099Data.per_llc || []).length > 1 && (
                        <div className="bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 border-b no-print">
                          LLC {idx + 1} of {form1099Data.per_llc.length}: {llcEntry.llc?.company_name || 'Unknown LLC'} — {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(llcEntry.total_amount)}
                        </div>
                      )}
                      <Form1099PrintView
                        payee={selected1099Payee}
                        llc={llcEntry.llc}
                        year={selected1099Year}
                        totalAmount={llcEntry.total_amount}
                        formType={selected1099Payee?.form_1099_type || '1099-NEC'}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Transactions Modal */}
      <Dialog open={showTransactions} onOpenChange={setShowTransactions}>
        <DialogContent className="max-w-4xl max-h-[80vh]" data-testid="transactions-modal">
          <DialogHeader>
            <DialogTitle>Transactions — {transPayeeName}</DialogTitle>
          </DialogHeader>
          {transLoading ? (
            <div className="py-8 text-center text-slate-500">Loading transactions...</div>
          ) : sortedTransactions.length === 0 ? (
            <div className="py-8 text-center text-slate-500">No transactions found for this payee.</div>
          ) : (
            <div className="overflow-y-auto" style={{ maxHeight: '60vh' }}>
              <table className="data-table w-full">
                <thead className="sticky top-0 z-10 bg-white">
                  <tr>
                    <th>Type</th>
                    <th>Ref</th>
                    <TransSortHeader col="date">Date</TransSortHeader>
                    <TransSortHeader col="payer">Payer</TransSortHeader>
                    <TransSortHeader col="memo">Memo</TransSortHeader>
                    <TransSortHeader col="amount" className="text-right">Amount</TransSortHeader>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedTransactions.map((t, i) => (
                    <tr key={i}>
                      <td>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded ${t.type === 'check' ? 'bg-blue-50 text-blue-700' : 'bg-orange-50 text-orange-700'}`}>
                          {t.type === 'check' ? 'Check' : 'Wire'}
                        </span>
                      </td>
                      <td className="text-sm">{t.ref}</td>
                      <td className="text-sm">{t.date ? new Date(t.date).toLocaleDateString() : '—'}</td>
                      <td className="text-sm font-medium">{t.payer}</td>
                      <td className="text-sm text-slate-600">{t.memo || '—'}</td>
                      <td className="text-sm text-right font-mono">${(t.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                      <td>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                          t.status === 'printed' || t.status === 'sent' ? 'bg-green-50 text-green-700' :
                          t.status === 'voided' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'
                        }`}>
                          {t.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="text-sm text-slate-500 mt-3 px-2">
                {sortedTransactions.length} transaction{sortedTransactions.length !== 1 ? 's' : ''} — Total: ${sortedTransactions.reduce((s, t) => s + (t.amount || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Archived Duplicate Dialog */}
      <AlertDialog open={!!archivedDupe} onOpenChange={(o) => { if (!o) setArchivedDupe(null); }}>
        <AlertDialogContent data-testid="archived-dupe-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Payee Already Exists (Archived)</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{archivedDupe?.name}</strong> is already in your payee list but has been archived. Would you like to restore it, or create a new separate payee?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setArchivedDupe(null)}>Cancel</AlertDialogCancel>
            <Button variant="outline" onClick={async () => {
              try {
                await payeeApi.toggleArchive(archivedDupe.id);
                toast.success(`${archivedDupe.name} restored from archive`);
                setArchivedDupe(null);
                setFormData(initialFormData);
                fetchData();
              } catch { toast.error('Failed to restore'); }
            }} data-testid="restore-archived-btn">
              <ArchiveRestore className="w-4 h-4 mr-2" /> Restore Existing
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
